import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidate,
  useCandidatesList,
  useDiagnosticBank,
  useEffectiveUser,
  useEnrollmentsForCandidate,
  useExamAttemptsForExpediente,
  useExpedienteForCandidateStandard,
} from "@/store/hooks";

function useQuery() {
  const [loc] = useLocation();
  return useMemo(() => {
    const s = typeof window !== "undefined" ? window.location.search : "";
    return new URLSearchParams(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc, typeof window !== "undefined" ? window.location.search : ""]);
}

export default function DiagnosticEvaluation() {
  const [, setLocation] = useLocation();
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("alumno"), [setRole]);

  const effective = useEffectiveUser();
  const candList = useCandidatesList();
  const candidateId =
    effective && effective.roles.includes("alumno") ? effective.id : candList[0]?.id;
  const me = useCandidate(candidateId);

  const enrollments = useEnrollmentsForCandidate(candidateId);
  const q = useQuery();
  const requested = q.get("codigo");
  const codigo = requested && enrollments.includes(requested) ? requested : enrollments[0];

  const bank = useDiagnosticBank(codigo);
  const exp = useExpedienteForCandidateStandard(candidateId, codigo);
  const attempts = useExamAttemptsForExpediente(exp?.id).filter((a) => a.kind === "diagnostico");
  const lastScore = attempts.length ? attempts[attempts.length - 1].score : null;

  const complete = useDemoStore((s) => s.completeDiagnostic);
  const addAttempt = useDemoStore((s) => s.addExamAttempt);
  const updateExpediente = useDemoStore((s) => s.updateExpediente);
  const logEvent = useDemoStore((s) => s.logEvent);

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResult, setShowResult] = useState<{ score: number } | null>(null);

  if (!me || !codigo) {
    return (
      <AppLayout breadcrumb={["Mi ruta", "Diagnóstico"]}>
        <Card><div style={{ padding: 24, color: "var(--cc-gray-600)" }}>
          {!me ? "No hay candidato activo." : "Sin estándar seleccionado o inscrito."}
        </div></Card>
      </AppLayout>
    );
  }

  const onSubmit = () => {
    const answered = bank.filter((q) => (answers[q.id] ?? "").trim().length > 0).length;
    const score = bank.length === 0 ? 0 : Math.round((answered / bank.length) * 100);
    if (exp) {
      addAttempt({
        id: `att-${Date.now().toString(36)}`,
        expedienteId: exp.id,
        candidatoId: me.id,
        estandar: codigo,
        kind: "diagnostico",
        fecha: new Date().toISOString().slice(0, 10),
        score,
        aprobado: true,
      });
      updateExpediente(exp.id, {
        diagnostico: { aplicado: true, puntaje: score, fecha: new Date().toISOString().slice(0, 10) },
      });
    }
    complete();
    logEvent({ actor: me.nombre, rol: "alumno", accion: `Aplicó diagnóstico (${score}/100)`, entidad: "expediente", entidadId: exp?.id ?? "-" });
    setShowResult({ score });
  };

  const onRetry = () => {
    setAnswers({});
    setShowResult(null);
  };

  return (
    <AppLayout breadcrumb={["Mi ruta", "Diagnóstico"]}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Evaluación diagnóstica · {codigo}</h1>
        <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
          {bank.length} reactivos · Puede repetir el diagnóstico tantas veces como lo necesite.
        </div>
      </div>

      {(showResult || lastScore != null) && !answers.__inprogress && (
        <Card style={{ background: "var(--cc-green-bg)", border: "1px solid #86efac", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CheckCircle2 size={22} color="var(--cc-green)" />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }} data-testid="text-diag-score">
                  Diagnóstico {showResult ? "completado" : "previo"} · puntaje {showResult ? showResult.score : lastScore} / 100
                </div>
                <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>
                  {attempts.length + (showResult ? 1 : 0)} intento(s) registrado(s) en este estándar.
                </div>
              </div>
            </div>
            {showResult && (
              <PrimaryButton variant="ghost" onClick={onRetry} testId="button-retry-diag">
                <RotateCcw size={13} /> Repetir
              </PrimaryButton>
            )}
          </div>
        </Card>
      )}

      <Card>
        <SectionTitle>Reactivos del estándar</SectionTitle>
        {bank.length === 0 ? (
          <div style={{ padding: 14, color: "var(--cc-gray-600)", fontSize: 13 }}>
            Este estándar aún no tiene reactivos diagnósticos configurados. El administrador puede agregarlos.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {bank.map((q, i) => (
              <div key={q.id} style={{ padding: 12, border: "1px solid var(--cc-gray-100)", borderRadius: 10 }} data-testid={`q-${q.id}`}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-teal)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{q.area} · Reactivo {i + 1}</div>
                <div style={{ fontSize: 13, color: "var(--cc-navy)", fontWeight: 600, marginBottom: 10 }}>{q.pregunta}</div>
                <input
                  value={answers[q.id] ?? ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  placeholder="Su respuesta..."
                  data-testid={`input-answer-${q.id}`}
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 13 }}
                />
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <PrimaryButton variant="ghost" onClick={() => setLocation("/alumno")} testId="button-back">← Volver al tablero</PrimaryButton>
          <div style={{ display: "flex", gap: 8 }}>
            <Link href={`/alumno/examen-teorico?codigo=${codigo}`}><PrimaryButton variant="ghost">Continuar al examen teórico →</PrimaryButton></Link>
            <PrimaryButton onClick={onSubmit} testId="button-submit-diag" disabled={bank.length === 0}>
              Enviar diagnóstico
            </PrimaryButton>
          </div>
        </div>
      </Card>
    </AppLayout>
  );
}
